import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { getNotificationIcon, getNotificationTitle } from '../../utils/notifications';

const getToastKey = (notification) =>
  notification?.id ||
  `${notification?.type || 'system_message'}::${notification?.message || notification?.content || ''}`;

const NotificationToast = () => {
  const { notifications } = useNotifications();
  const [toasts, setToasts] = useState([]);
  const seenNotificationKeysRef = useRef(new Set());
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!Array.isArray(notifications) || notifications.length === 0) {
      return;
    }

    if (!hasInitializedRef.current) {
      notifications.forEach((notification) => {
        seenNotificationKeysRef.current.add(getToastKey(notification));
      });
      hasInitializedRef.current = true;
      return;
    }

    const newNotifications = notifications.filter((notification) => {
      const key = getToastKey(notification);
      if (seenNotificationKeysRef.current.has(key)) {
        return false;
      }

      seenNotificationKeysRef.current.add(key);
      return true;
    });

    newNotifications.forEach((notification) => {
      const toast = {
        id: getToastKey(notification),
        type: notification.type,
        title: getNotificationTitle(notification.type),
        message: notification.message || notification.content || 'You have a new notification.',
        timestamp: Date.now()
      };

      setToasts((prev) => [toast, ...prev.filter((existingToast) => existingToast.id !== toast.id)]);

      setTimeout(() => {
        setToasts((prev) => prev.filter((existingToast) => existingToast.id !== toast.id));
      }, 5000);
    });
  }, [notifications]);

  const removeToast = (toastId) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="bg-white border border-gray-200 rounded-lg p-4 max-w-sm w-full transform transition-all duration-300 ease-in-out"
          style={{
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">{getNotificationIcon(toast.type)}</div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{toast.title}</h4>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{toast.message}</p>
                </div>

                <button
                  onClick={() => removeToast(toast.id)}
                  className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationToast;
